import React from 'react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { Settings, ClipboardCheck } from 'lucide-react';

interface Step {
  step: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface AddPerformanceStepHeaderProps {
  currentStep: number;
  completedSteps?: number[];
  // eslint-disable-next-line no-unused-vars
  onStepClick?: (step: number) => void;
}

const steps: Step[] = [
  {
    step: 1,
    title: 'Brand & Platform Details',
    description: 'Select brand, add platforms and configure account details',
    icon: <Settings className="w-5 h-5" />,
  },
  {
    step: 2,
    title: 'Review & Submit',
    description: 'Review and submit your performance accounts',
    icon: <ClipboardCheck className="w-5 h-5" />,
  },
];

export const AddPerformanceStepHeader: React.FC<AddPerformanceStepHeaderProps> = ({
  currentStep,
  completedSteps = [],
  onStepClick,
}) => {
  const handleStepClick = (step: number) => {
    if (onStepClick) {
      const lastCompletedStep =
        completedSteps.length > 0 ? Math.max(...completedSteps) : -1;
      if (
        completedSteps.includes(step - 1) ||
        step === lastCompletedStep + 2
      ) {
        onStepClick(step);
      }
    }
  };

  const isStepClickable = (step: number) => {
    if (!onStepClick) return false;
    const lastCompletedStep =
      completedSteps.length > 0 ? Math.max(...completedSteps) : -1;
    return (
      completedSteps.includes(step - 1) || step === lastCompletedStep + 2
    );
  };

  return (
    <div className="flex justify-between w-full items-center">
      <div className="flex-1 flex justify-center">
        <div className="inline-block border border-gray-200 rounded-lg bg-[#FBFBFA]">
          <TooltipProvider>
            <ul className="flex items-center m-0 p-0 list-none h-12">
              {steps.map((step, index) => {
                const isActive = currentStep === step.step;
                const clickable = isStepClickable(step.step);

                return (
                  <li
                    key={step.step}
                    className={cn(
                      'flex items-center h-full',
                      index > 0 && 'border-l border-gray-200',
                      !isActive && 'opacity-60'
                    )}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'flex items-center justify-center h-12 px-3 transition-all duration-300 ease-in-out',
                            isActive
                              ? 'bg-white min-w-[8.25rem] max-w-[15rem] border border-white/10 rounded'
                              : 'w-[5.5rem]',
                            clickable && 'cursor-pointer hover:bg-gray-100',
                            !clickable && 'cursor-not-allowed'
                          )}
                          onClick={() =>
                            clickable && handleStepClick(step.step)
                          }
                        >
                          {/* Icon Circle */}
                          <div
                            className={cn(
                              'flex items-center justify-center rounded-full flex-shrink-0 h-8 w-8 transition-all duration-300 ease-in-out',
                              isActive
                                ? 'bg-[#232e27] text-white'
                                : 'text-gray-400'
                            )}
                          >
                            {step.icon}
                          </div>

                          {/* Step Info - Only show for active step */}
                          {isActive && (
                            <div className="flex flex-col justify-center ml-2 h-full overflow-hidden">
                              <p className="text-xs font-normal leading-[130%] mb-0.5 text-black opacity-60">
                                Step {step.step}/{steps.length}
                              </p>
                              <div className="text-sm font-semibold leading-[130%] whitespace-nowrap text-black/70">
                                {step.title}
                              </div>
                            </div>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>{step.title}</TooltipContent>
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
};
